import { FC, useMemo } from 'react';
import { TariffCard } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-card';
import { ViewStackViewWithNavigation } from '@modules/get-slash-internet-card/components/dialog-views/view-stack-view-with-navigation';
import { useClientTranslation } from '@lib/i18n/client';
import { Locale } from '@tuic/i18n';
import { getPriceExpression } from '@modules/get-slash-internet-card/lib/utils';
import { useViewStack } from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { TariffDetails } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-details';
import { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type { GetInternetDataTariff } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import { GetSlashTariff } from '@modules/get-slash-internet-card/lib/get-slash';
import { useBookableTariffs } from '@modules/get-slash-internet-card/lib/use-bookable-tariffs';
import { DialogBody } from '@modules/get-slash-internet-card/components/dialog-views/dialog-body';
import { RefreshDialogHandler } from '@modules/get-slash-internet-card/components/dialog-view-renderer';

export type CombinedInternetTariff = Omit<GetInternetDataTariff, 'text'> & {
  text?: GetInternetDataTariff['text'] | string;
  getSlash: GetSlashTariff;
};

type Props = {
  locale: Locale;
  tariffs?: GetInternetDataTariff[];
  fallbackImages?: Pick<
    GetInternetDataTariff,
    'imageListing' | 'imageHero' | 'imageHeroMd'
  >;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints?: boolean;
  refreshDialog: RefreshDialogHandler;
};

export const TariffListing: FC<Props> = props => {
  const {
    locale,
    tariffs = [],
    fallbackImages,
    getSlashCredentials,
    useDebugEndpoints,
    refreshDialog,
  } = props;

  const { push } = useViewStack();

  const { t } = useClientTranslation();

  const bookableTariffs = useBookableTariffs({
    credentials: getSlashCredentials,
    useDebugEndpoints,
  });

  const { tariffs: getSlashTariffs, ready } = bookableTariffs;

  const combinedTariffs = useMemo(() => {
    if (!ready) {
      return [];
    }

    return getSlashTariffs
      .map((tariff): CombinedInternetTariff | undefined => {
        const contentfulTariff = tariffs.find(contentfulTariff => {
          return contentfulTariff.externalId === tariff.id;
        });

        if (contentfulTariff) {
          return {
            ...contentfulTariff,
            getSlash: tariff,
          };
        }

        return {
          ...fallbackImages,
          externalId: tariff.id,
          title: tariff.name,
          text: tariff.description,
          teaser: tariff.description,
          getSlash: tariff,
        };
      })
      .filter((combined): combined is CombinedInternetTariff => {
        if (typeof combined === 'undefined') {
          return false;
        }

        return typeof combined.getSlash !== 'undefined';
      });
  }, [getSlashTariffs, tariffs, fallbackImages, ready]);

  const handleBuy = async (tariff: CombinedInternetTariff) => {
    const response = await bookableTariffs.book(tariff.externalId);

    if (response) {
      return true;
    }

    return t(
      'Bitte versuchen Sie es später erneut. Sollte der Fehler bestehen bleiben, wenden Sie sich bitte an das Mein Schiff ® Service Team.',
    );
  };

  const showTariffDetails = (tariff: CombinedInternetTariff) => {
    push(
      <TariffDetails
        locale={locale}
        tariff={tariff}
        buyTariff={handleBuy}
        refreshDialog={refreshDialog}
      />,
    );
  };

  return (
    <DialogBody colored>
      <ViewStackViewWithNavigation title={t('Alle Internetpakete')}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {!ready && (
            <>
              {Array(4)
                .fill(null)
                .map((_, idx) => (
                  <li key={idx}>
                    <TariffCard fallback />
                  </li>
                ))}
            </>
          )}

          {ready && (
            <>
              {combinedTariffs.map(tariff => (
                <li key={tariff.externalId}>
                  <TariffCard
                    image={tariff.imageListing}
                    headline={tariff.title ?? tariff.getSlash.name}
                    text={tariff.teaser}
                    priceExpression={getPriceExpression(locale, tariff)}
                    tag={tariff.tag}
                    asChild
                  >
                    <button onClick={() => showTariffDetails(tariff)} />
                  </TariffCard>
                </li>
              ))}
            </>
          )}
        </ul>
      </ViewStackViewWithNavigation>
    </DialogBody>
  );
};
